import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useRef, useEffect, useState } from 'react';
import LoadingBar from 'react-top-loading-bar';
import { Helmet } from 'react-helmet';

// Components
import ScrollToTop from './ScrollToTop';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Pages
import LandingPage from './pages/LandingPage';
import EventDetails from './pages/EventDetails';
import RegisterEvent from './pages/RegisterEvent';
import ForgotPassword from './pages/ForgotPassword';
import PaymentStatus from './pages/PaymentStatus';
import Account from './pages/Account';
import Login from './pages/Login';
import Register from './pages/Register';
import ContactUs from './pages/ContactUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AboutUs from './pages/AboutUs';
import RefundPolicy from './pages/RefundPolicy';
import PaymentPolicy from './pages/PaymentPolicy';
import EventRules from './pages/EventRules';

/**
 * App Component
 * 
 * The main application component that sets up routing, global components,
 * and the overall layout structure. Styled to match the main YouthXConnect site.
 * 
 * Enhanced PWA Features:
 * - Optimized for mobile-first design
 * - Strategic notification prompts (only for registered users with installed PWA)
 * - Improved offline experience with better user feedback
 * - Professional UI/UX matching YouthXConnect branding
 * 
 * @returns {JSX.Element} - Rendered application
 */
function App() {
  // Create a ref for LoadingBar for smooth page transitions
  const loadingBarRef = useRef(null);
  
  // State to track online/offline status for better user experience
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  /**
   * Effect to handle online/offline status changes
   * Provides user feedback about connectivity without intrusive notifications
   */
  useEffect(() => {
    // Function to update online status
    const handleOnlineStatusChange = () => {
      setIsOnline(navigator.onLine);
      
      // Only show connectivity notifications if user has granted permission
      // and we're not spamming them with system notifications
      if ('Notification' in window && Notification.permission === 'granted') {
        if (navigator.onLine) {
          // Subtle online notification - only if user was offline for a while
          const wasOffline = localStorage.getItem('wasOffline');
          if (wasOffline === 'true') {
            new Notification('Connection restored', {
              body: 'YouthXConnect Events is back online',
              icon: '/logo192.png',
              tag: 'connectivity' // Prevents duplicate notifications
            });
            localStorage.removeItem('wasOffline');
          }
        } else {
          // Mark as offline for future reference
          localStorage.setItem('wasOffline', 'true');
          new Notification('You are offline', {
            body: 'Some features may be limited until you reconnect',
            icon: '/logo192.png',
            tag: 'connectivity'
          });
        }
      }
    };
    
    // Add event listeners for online/offline events
    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);
    
    // Clean up event listeners on component unmount
    return () => {
      window.removeEventListener('online', handleOnlineStatusChange);
      window.removeEventListener('offline', handleOnlineStatusChange);
    };
  }, []);

  return (
    <Router>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#4f46e5" />
        <meta name="description" content="YouthXConnect Events - Empowering youth through engaging events and workshops" />
        <link rel="icon" href="/logo-favicon-yxc.png" />
        <title>YouthXConnect Events</title>
      </Helmet>
      
      {/* Scroll to top on route change for better UX */}
      <ScrollToTop />
      
      {/* Global Loading Bar for smooth page transitions */}
      <LoadingBar color='#3b82f6' ref={loadingBarRef} height={3} />
      
      {/* Offline indicator - non-intrusive but informative */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-500 text-white text-center py-1 text-sm">
          You are currently offline. Some features may be limited.
        </div>
      )}
      
      <div className="flex flex-col min-h-screen bg-white">
        {/* Enhanced Navbar with strategic PWA install prompts */}
        <Navbar loadingBarRef={loadingBarRef} />
        
        {/* Main content area with proper spacing */}
        <main className={`flex-grow pt-5 ${!isOnline ? 'mt-6' : ''}`}>
          <Routes>
            <Route path="/" element={<LandingPage loadingBarRef={loadingBarRef} />} />
            <Route path=":eventId" element={<EventDetails loadingBarRef={loadingBarRef} />} />
            <Route path=":eventId/register" element={<RegisterEvent loadingBarRef={loadingBarRef} />} />
            <Route path="/forgot-password" element={<ForgotPassword loadingBarRef={loadingBarRef} />} />
            <Route path="/payment-status" element={<PaymentStatus loadingBarRef={loadingBarRef} />} />
            <Route path="/account" element={<Account loadingBarRef={loadingBarRef} />} />
            <Route path="/login" element={<Login loadingBarRef={loadingBarRef} />} />
            <Route path="/reset-password" element={<ForgotPassword loadingBarRef={loadingBarRef} />} />
            <Route path="/register" element={<Register loadingBarRef={loadingBarRef} />} />
            <Route path="/contact" element={<ContactUs loadingBarRef={loadingBarRef} />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy loadingBarRef={loadingBarRef} />} />
            <Route path="/terms-of-service" element={<TermsOfService loadingBarRef={loadingBarRef} />} />
            <Route path="/about" element={<AboutUs loadingBarRef={loadingBarRef} />} />
            <Route path="/refund-policy" element={<RefundPolicy loadingBarRef={loadingBarRef} />} />
            <Route path="/payment-policy" element={<PaymentPolicy loadingBarRef={loadingBarRef} />} />
            <Route path="/event-rules" element={<EventRules loadingBarRef={loadingBarRef} />} />
          </Routes>
        </main>
        
        {/* Footer */}
        <Footer />
      </div>
    </Router>
  );
}

export default App;